import { RiDownloadLine } from '@remixicon/react';
import type { ReactNode } from 'react';

import * as history from '@shared/router/location';

import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';

import type { Key } from '@portal/pkg/api/types';
import { downloadPrivateKey } from '@portal/pkg/crypto/keypair';
import * as routes from '@portal/pkg/routes';
import { CodeBlock, CopyableValue } from '@portal/views/_shared/code-block';
import { Notice } from '@portal/views/_shared/notice';
import { PageSection } from '@portal/views/_shared/page';
import { formatDateTime } from '@portal/views/keys/_shared';

import css from './KeyIssued.module.css';

interface KeyIssuedProps {
	issued: Key;
	/**
	 *	Present only when this browser generated the pair. Held by the caller
	 *	for exactly as long as this screen is up.
	 */
	privateKey: Nullable<string>;
}

/**
 *	The after-state of creating or rotating a key. Not a route of its own: the
 *	private key cannot survive a navigation, and a URL anyone could return to
 *	would imply it can.
 */
export function KeyIssued({ issued, privateKey }: KeyIssuedProps): ReactNode {
	return (
		<div className={css.issued}>
			{privateKey && (
				<PageSection>
					<Notice variant="warning" title="Save the private key now">
						360Player never received it and cannot show it again. Leave this
						page and it is gone — you would have to rotate the key.
					</Notice>
					<CodeBlock
						name="privateKey"
						value={privateKey}
						rows={7}
						action={
							<Button
								variant="primary"
								label="Download .pem"
								leadingIcon={<RiDownloadLine />}
								onClick={() => downloadPrivateKey(issued.name, privateKey)}
							/>
						}
					/>
				</PageSection>
			)}

			<PageSection
				title="Key ID"
				description="Not a secret. It is the identity your server signs as — the signature is what proves it is you, and it goes in the iss and sub claims of the assertion.">
				<CopyableValue name="keyId" value={issued.id} />
			</PageSection>

			<PageSection
				title="Your first call"
				description="Exchange a signed assertion for a token, then read with the token. Tokens last an hour.">
				<CodeBlock name="firstCall" value={curlExample(issued)} rows={7} />
			</PageSection>

			<Card>
				<div className={css.done}>
					<p>Expires {formatDateTime(issued.expiresAt)}.</p>
					<Button
						variant="primary"
						label="I have saved the private key"
						onClick={() => history.push(routes.Keys())}
					/>
				</div>
			</Card>
		</div>
	);
}

function curlExample(key: Key): string {
	return [
		'curl -X POST https://publicapi.360player.com/oauth/token \\',
		'  -d grant_type=client_credentials \\',
		'  -d client_assertion_type=urn:ietf:params:oauth:client-assertion-type:jwt-bearer \\',
		'  -d client_assertion=$SIGNED_JWT \\',
		'  -d group_id=$GROUP_ID \\',
		`  -d scope="${key.capabilities.join(' ')}"`,
	].join('\n');
}
